import type { OrderListItem, OrderStatusLog } from '@agos/api-client/model'
import {
  type FiskalDocumentEvent,
  isKnownPaymentMethod,
  paymentLabel,
  tt,
} from '@agos/shared'

/**
 * The "História zmen" block under an expanded archive row.
 *
 * `/orders/{id}/edit-logs` returns one record per edit, each carrying a
 * `changes` map of `field → { from, to }`. Two fields do not fit that shape —
 * `items` and `addedItems` hold arrays of lines — and legacy special-cases them
 * inline. Pulled out here so the shape handling is testable, because an edit log
 * that renders `[object Object]` is an audit trail nobody can read.
 */

export interface EditLogChange {
  from?: unknown
  to?: unknown
}

export interface EditLogEntry {
  id?: number
  employeeName?: string
  employeeRole?: string
  createdAt?: string
  changes?: Record<string, EditLogChange>
}

interface EditLogLine {
  qty?: number
  name?: string
  /** §9.690 - written by the server only when the line had one. */
  note?: string | null
}

const FIELD_LABELS: Record<string, string> = {
  items: tt('Položky zmenené'),
  addedItems: tt('Pridané položky'),
  notes: tt('Poznámka'),
  customerAddress: tt('Adresa'),
  customerName: tt('Meno'),
  customerPhone: tt('Telefón'),
  customerEmail: tt('Email'),
  discount: tt('Zľava'),
  deliveryCost: tt('Dovoz'),
  // §9.446, FB-763 - the five `BACKEND.md` §10A.208 started recording. Without a label each of
  // these would render as its own camelCase name, which `editLogFieldLabel` falls back to.
  paymentMethod: tt('Spôsob platby'),
  paymentStatus: tt('Stav platby'),
  manualDiscountType: tt('Ručná zľava - typ'),
  manualDiscountValue: tt('Ručná zľava - hodnota'),
  manualDiscountOperator: tt('Ručnú zľavu zadal'),
  // §9.458, BACKEND.md §10A.220 - the owner: *"when its changed from reštaurácia to osobný odber,
  // or.. basically changed between channels, make sure it will be also visible as change"*.
  type: tt('Kanál'),
  /* §9.468, BACKEND.md §10A.229 - owner decision #60, *store who and why*. The only field in this map
   * that is not a column on `orders` changing value: the row is written by the close itself, `from` is
   * always null and `to` is the reason the authoriser typed. Without a label it would render as
   * `closedWithoutDocument`, which is the fallback below rather than a defect - but this is the one row
   * in the trail whose whole purpose is to be read by somebody asking what happened. */
  closedWithoutDocument: tt('Zatvorené bez dokladu'),
  /* §9.690, BACKEND.md §10A.349 - a note-only change to existing lines, written by line. Without a label
   * it would render as `lineNotes`. */
  lineNotes: tt('Poznámka k položke'),
}

export function editLogFieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field
}

export type DescribedChange =
  | { kind: 'lines'; before: string; after: string }
  | { kind: 'added'; added: string }
  | { kind: 'scalar'; before: string; after: string }

/**
 * `5× Margherita, 2× Cola`. An em dash when there is nothing, so a row that
 * cleared its items reads as a change rather than as a gap.
 */
export function formatLines(lines: unknown, prefix = ''): string {
  if (!Array.isArray(lines) || lines.length === 0) return '-'
  return (lines as EditLogLine[])
    .map((line) => `${prefix}${line.qty ?? 0}× ${line.name ?? ''}${noteText(line)}`.trim())
    .join(', ')
}

/**
 * ` (bez cibule)` after a line that carried a note, nothing after one that did not - §9.690.
 *
 * **Without it a note changed beside a quantity reads as an unchanged line**: the server writes the note
 * into both sides of the `items` entry and this printed only the quantity and the name.
 */
function noteText(line: EditLogLine): string {
  return typeof line.note === 'string' && line.note.trim() !== '' ? ` (${line.note.trim()})` : ''
}

/**
 * `Margherita: bez cibule, Diavola: -` - a `lineNotes` entry, one line per changed note - §9.690.
 *
 * A removed note prints the dash every other cleared value prints, so *the note was taken off* reads
 * as a change rather than as a gap.
 */
function lineNotesText(lines: unknown): string {
  if (!Array.isArray(lines) || lines.length === 0) return '-'
  return (lines as EditLogLine[])
    .map((line) => `${line.name ?? ''}: ${scalarText(line.note)}`)
    .join(', ')
}

export function describeEditLogChange(
  field: string,
  change: EditLogChange,
  /**
   * Slug to name, for the channel - §9.458.
   *
   * **Passed in rather than looked up here, because channels are franchise-configured.** There is no
   * static map to hold: a franchise renames *Osobný odber* and the log has to follow. Optional and
   * defaulting to empty, so the slug still prints and the change is still **visible** - which is what
   * the owner asked for - on a screen that has not been given the list.
   */
  channelNames: Record<string, string> = {},
): DescribedChange {
  if (field === 'items') {
    return {
      kind: 'lines',
      before: formatLines(change.from),
      after: formatLines(change.to),
    }
  }
  if (field === 'addedItems') {
    return { kind: 'added', added: formatLines(change.to, '+ ') }
  }
  if (field === 'lineNotes') {
    return { kind: 'scalar', before: lineNotesText(change.from), after: lineNotesText(change.to) }
  }
  if (field === 'paymentMethod') {
    return {
      kind: 'scalar',
      before: paymentText(change.from),
      after: paymentText(change.to),
    }
  }
  if (field === 'type') {
    return {
      kind: 'scalar',
      before: channelText(change.from, channelNames),
      after: channelText(change.to, channelNames),
    }
  }
  return {
    kind: 'scalar',
    before: scalarText(change.from),
    after: scalarText(change.to),
  }
}

/**
 * A payment method as a word - §9.446, FB-763.
 *
 * **An unknown slug prints as itself, which is the opposite of what a receipt wants.**
 * `paymentLabel` answers *Karta* for anything it does not recognise, and on this panel that would
 * turn `card_online → bank_transfer` into *CardPay Online → Karta* - hiding the change the owner
 * asked to be able to see. The owner's data holds only the four slugs the map knows, so this branch
 * is unreachable today; it is here because an audit trail that guesses is worse than none.
 */
function paymentText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-'
  const slug = typeof value === 'string' ? value : ''
  if (slug === '') return scalarText(value)
  return isKnownPaymentMethod(slug) ? paymentLabel(slug) : slug
}

/**
 * A sales channel as its own name - §9.458.
 *
 * **The slug when the name is unknown, never a guess.** `paymentText` above makes the same choice for
 * the same reason, in its own words: *an audit trail that guesses is worse than none*. A franchise
 * that has deleted a channel still has orders that were on it, and printing `osobny_odber` is honest
 * where printing the nearest surviving channel's name would be a lie about which board the order was
 * moved to.
 */
function channelText(value: unknown, names: Record<string, string>): string {
  if (value === null || value === undefined || value === '') return '-'
  const slug = typeof value === 'string' ? value : ''
  if (slug === '') return scalarText(value)
  return names[slug] ?? slug
}

/**
 * `String(null)` is `"null"` and `String(undefined)` is `"undefined"`, both of
 * which have appeared on this screen. An absent value is an em dash; `0` and
 * `false` are values and print as themselves.
 *
 * FB-042: legacy calls `String()` on whatever the field held, so a change to a
 * field carrying an object — `manualDiscount`, `sizeData` — renders as
 * `[object Object]` on both sides of the arrow. Serialised instead, because the
 * point of this block is that somebody can read what changed.
 */
function scalarText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'object') return JSON.stringify(value)
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- objects are handled above; what reaches here is a primitive
  return String(value)
}

/**
 * One row of *História zmien*, from either of the two tables that record one - §9.446, FB-763.
 *
 * **A status change is not a `field → {from, to}`**, so it is its own kind rather than a fake change
 * entry. Forcing it into the edit-log shape would mean inventing a field name for it and then
 * special-casing that name back out again on the screen.
 */
export type HistoryRow =
  | { kind: 'edit'; at: string; entry: EditLogEntry }
  | { kind: 'status'; at: string; entry: OrderStatusLog }
  /**
   * A fiscal document printed or cancelled - §9.570, FB-864.
   *
   * **The third trail.** §9.446 merged the edit log and the status log; `fiskal_documents` has been
   * recording every print and every reversal since §10A.191 with the figure the register was told to
   * print, and **nothing could read it back** until §10A.294. The owner asked for it here by name.
   *
   * A document contributes **two** rows when it was reversed - the print and the cancellation - so
   * that a receipt printed, voided and re-issued reads in the order it happened.
   */
  | { kind: 'fiskal'; at: string; entry: FiskalDocumentEvent }

/**
 * Both trails, in time order - §9.446, FB-763.
 *
 * **The owner asked for four kinds of change in one place**, and two of them live in
 * `order_edit_logs` while status changes live in `order_status_logs` - **20 690 rows over 10 692
 * orders, each naming who confirmed it, and nothing in the frontend read them.** Two panels would
 * make *what happened to this order, in order* something the reader has to assemble; this is that
 * assembly, here rather than in the component so the ordering can be tested.
 *
 * **Oldest first**, matching the status endpoint's own `OrderByCreatedAtAsc` and the way somebody
 * reads a sequence of events. A row with no timestamp sorts last rather than being dropped: it is
 * still a change that happened, and an audit panel that silently omits rows is worse than one with
 * an undated entry at the bottom.
 */
export function mergedHistory(
  editLogs: readonly EditLogEntry[],
  statusLogs: readonly OrderStatusLog[],
  fiskalEvents: readonly FiskalDocumentEvent[] = [],
): HistoryRow[] {
  const rows: HistoryRow[] = [
    ...editLogs.map((entry): HistoryRow => ({ kind: 'edit', at: entry.createdAt ?? '', entry })),
    ...statusLogs.map((entry): HistoryRow => ({
      kind: 'status',
      at: entry.createdAt ?? '',
      entry,
    })),
    /* §9.570 - defaulted rather than required, so every existing caller and every existing case keeps
     * its two-trail behaviour unchanged. The wiring guard in `apps/manager` asserts the third argument
     * really is passed, because an optional parameter dropped at the call site compiles silently -
     * §9.518's rule, which this repository has recorded four times. */
    ...fiskalEvents.map((entry): HistoryRow => ({ kind: 'fiskal', at: entry.at, entry })),
  ]

  return rows.sort((left, right) => {
    if (left.at === right.at) return 0
    if (left.at === '') return 1
    if (right.at === '') return -1
    return left.at < right.at ? -1 : 1
  })
}

/**
 * The sentence the edited-order badge carries — §9.286, FB-443.
 *
 * Here rather than beside the badge because `OrderRow.tsx` exports components, and a non-component
 * export in that file costs a `react-refresh/only-export-components` warning. `editLogFieldLabel`
 * above is the same kind of thing about the same subject, so this is its neighbour.
 */
export function editedFlagLabel(order: OrderListItem): string {
  return order.updatedByName
    ? `${tt('Upravil')}: ${order.updatedByName}`
    : tt('Objednávka bola upravená')
}
